import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import ZoomLevel from "./ZoomLevel/ZoomLevel";
import Legend from "./Legend/Legend";
import MapSheet from "./MapSheet/MapSheet";
import SheetBottom from "../../../../../../components/SheetBottom/SheetBottom";
import { MobileFSExit } from "./MobileFSExit";

import "./actionsProvider.scss";
import useSeatmapStore from "../../../../../../../../stores/seatmapStore";

// Create the context
const ActionsContext = createContext(undefined);

export const useActions = () => {
  const context = useContext(ActionsContext);
  if (!context) {
    throw new Error("Context must be used within a ActionsProvider");
  }
  return context;
};

// Create the provider component
export const ActionsProvider = ({
  handleClose,
  loadingComplete,
  mapError,
  checkout,
  checkingAvailability,
  children,
}) => {
  // Zustand
  const totalCount = useSeatmapStore((state) => state.totalCount);
  const totalPrice = useSeatmapStore((state) => state.totalPrice);
  const containerRef = useRef(null);
  const dialogRef = useRef(null);
  const stageRef = useRef(null);
  const originalScaleRef = useRef(1);
  const SCALE_FACTOR = 1.08; // Increased for more responsive feel
  const SCALE_LIMITS = { MIN: 0, MAX: 350 };
  const [desktopSheetOpen, setDesktopSheetOpen] = useState(false);

  // Open a sheet when user has selected one or more seats
  useEffect(() => {
    if (totalCount > 0) {
      if (window.innerWidth > 768) {
        setDesktopSheetOpen(true);
      }
    } else {
      setDesktopSheetOpen(false);
      closeMobileSheet();
    }
  }, [totalCount]);

  const openMobileSheet = () => {
    let dialog = dialogRef.current;
    if (dialog) {
      dialog.showModal();
    }
  };

  const closeMobileSheet = () => {
    let dialog = dialogRef.current;
    if (dialog) {
      dialog.close();
    }
  };

  const calculateScalePercentage = (scaleToUse) => {
    const ratio = scaleToUse / originalScaleRef.current;
    const percentage = (ratio * 100 - 100) / 10;
    return Number(percentage.toFixed(0));
  };

  return (
    <ActionsContext.Provider
      value={{
        containerRef,
        stageRef,
        originalScaleRef,
        calculateScalePercentage,
        SCALE_FACTOR,
        SCALE_LIMITS,
      }}
    >
      <div id="map" ref={containerRef}>
        <MobileFSExit handleClose={handleClose} />
        {children}
        {loadingComplete && !mapError && (
          <ControlsContainer
            desktopSheetOpen={desktopSheetOpen}
            totalCount={totalCount}
            openMobileSheet={openMobileSheet}
            checkout={checkout}
            checkingAvailability={checkingAvailability}
            totalPrice={totalPrice}
          />
        )}
      </div>
      <dialog ref={dialogRef} className="mobile-map-sheet-dialog">
        <MapSheet
          canGoBack={true}
          goBack={closeMobileSheet}
          totalCount={totalCount}
          checkout={checkout}
          checkingAvailability={checkingAvailability}
          totalPrice={totalPrice}
        />
      </dialog>
    </ActionsContext.Provider>
  );
};

const ControlsContainer = ({
  desktopSheetOpen,
  totalCount,
  totalPrice,
  openMobileSheet,
  checkout,
  checkingAvailability,
}) => (
  <div className="controls-container">
    <div className="vjx-cc-outer">
      <Legend />
      <div className="vjx-cc-inner">
        <ZoomLevel />
        {desktopSheetOpen && (
          <MapSheet
            totalCount={totalCount}
            checkout={checkout}
            checkingAvailability={checkingAvailability}
            totalPrice={totalPrice}
          />
        )}
      </div>
    </div>
    <div className="mobile-only">
      {totalCount > 0 && (
        <SheetBottom
          VSButton={true}
          VSClick={openMobileSheet}
          selectedTicketCount={totalCount}
          totalTicketsPrice={totalPrice}
          checkout={() => checkout(false)} // isQuickpick
          checkingAvailability={checkingAvailability}
        />
      )}
    </div>
  </div>
);
